import React, { useId } from "react";

import { cn } from "../../../../../lib/utils";
import { Switch } from "../../../../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../ui/tooltip";
import { PlatformLabelBadges } from "../PlatformLabelBadges/PlatformLabelBadges";

interface SwitchFieldRowProps {
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  helper?: React.ReactNode;
  info?: { label: string; content: React.ReactNode };
  disabled?: boolean;
  selectedPlatform?: string;
  platforms?: string[];
  trailing?: React.ReactNode;
  id?: string;
  className?: string;
  children?: React.ReactNode;
  "data-testid"?: string;
}

export const InfoTooltip: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-neutral-500 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <i className="fa-regular fa-circle-info text-sm" aria-hidden="true" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[280px] text-left font-medium">
      {children}
    </TooltipContent>
  </Tooltip>
);

/**
 * One composer setting: a switch, its label, the platforms it applies to on the
 * linked tab, and a line of helper text aligned under the label.
 */
export const SwitchFieldRow: React.FC<SwitchFieldRowProps> = ({
  label,
  checked,
  onCheckedChange,
  helper,
  info,
  disabled,
  selectedPlatform,
  platforms,
  trailing,
  id,
  className,
  children,
  "data-testid": testId,
}) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helperId = helper ? `${fieldId}-helper` : undefined;

  return (
    <div className={cn("mb-4 flex flex-col", className)} data-testid={testId}>
      <div className="flex min-h-5 items-center gap-3">
        <Switch
          id={fieldId}
          size="md"
          checked={checked}
          disabled={disabled}
          aria-describedby={helperId}
          onCheckedChange={onCheckedChange}
        />
        <div className="flex min-w-0 items-center gap-2">
          <label
            htmlFor={fieldId}
            className={cn(
              "m-0 font-inter text-sm font-medium leading-5",
              disabled
                ? "cursor-not-allowed text-neutral-500"
                : "cursor-pointer text-neutral-800"
            )}
          >
            {label}
          </label>
          {platforms ? (
            <PlatformLabelBadges
              selectedPlatform={selectedPlatform}
              platforms={platforms}
            />
          ) : null}
          {trailing}
          {info ? (
            <InfoTooltip label={info.label}>{info.content}</InfoTooltip>
          ) : null}
        </div>
      </div>
      {helper ? (
        <p
          id={helperId}
          className="m-0 mt-0.5 pl-12 font-inter text-xs leading-5 text-neutral-600"
        >
          {helper}
        </p>
      ) : null}
      {children ? <div className="mt-3 pl-12">{children}</div> : null}
    </div>
  );
};
